import React from 'react';

export interface QuizSettingsValue {
  timerMinutes: string;
  isRetake: boolean;
  isScoreDisplayed: boolean;
  isAnswerDisplayed: boolean;
  passDegree: string;
}

export interface QuizSettingsPayload {
  quizTimer: number | null;
  isRetake: boolean;
  isScoreDisplayed: boolean;
  isAnswerDisplayed: boolean;
  passDegree: number;
}

export const emptyQuizSettings = (): QuizSettingsValue => ({
  timerMinutes: '',
  isRetake: false,
  isScoreDisplayed: true,
  isAnswerDisplayed: false,
  passDegree: '50',
});

export function quizSettingsFromLesson(raw: any): QuizSettingsValue {
  const timer = raw?.quizTimer ?? raw?.QuizTimer;
  const pass = raw?.passDegree ?? raw?.PassDegree;
  return {
    timerMinutes: timer === null || timer === undefined || timer === '' ? '' : String(timer),
    isRetake: Boolean(raw?.isRetake ?? raw?.IsRetake),
    isScoreDisplayed: Boolean(raw?.isScoreDisplayed ?? raw?.IsScoreDisplayed),
    isAnswerDisplayed: Boolean(raw?.isAnswerDisplayed ?? raw?.IsAnswerDisplayed),
    passDegree: pass === null || pass === undefined || pass === '' ? '50' : String(pass),
  };
}

export function parseQuizSettings(
  value: QuizSettingsValue
): { ok: true; settings: QuizSettingsPayload } | { ok: false; error: string } {
  const timerTrim = value.timerMinutes.trim();
  let quizTimer: number | null = null;
  if (timerTrim !== '') {
    const mins = Number(timerTrim);
    if (!Number.isInteger(mins) || mins < 1) {
      return { ok: false, error: 'أدخل مدة صحيحة بالدقائق، أو اترك الحقل فارغاً بدون مؤقت' };
    }
    quizTimer = mins;
  }

  const passTrim = value.passDegree.trim();
  const passDegree = Number(passTrim);
  if (passTrim === '' || Number.isNaN(passDegree) || passDegree < 0 || passDegree > 100) {
    return { ok: false, error: 'درجة النجاح يجب أن تكون بين 0 و 100' };
  }

  return {
    ok: true,
    settings: {
      quizTimer,
      isRetake: value.isRetake,
      isScoreDisplayed: value.isScoreDisplayed,
      isAnswerDisplayed: value.isAnswerDisplayed,
      passDegree,
    },
  };
}

interface QuizSettingsFieldsProps {
  value: QuizSettingsValue;
  onChange: (value: QuizSettingsValue) => void;
  disabled?: boolean;
}

export const QuizSettingsFields: React.FC<QuizSettingsFieldsProps> = ({ value, onChange, disabled }) => {
  const update = (patch: Partial<QuizSettingsValue>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h3 className="text-sm font-semibold text-gray-900">إعدادات الاختبار</h3>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">المدة بالدقائق</label>
        <input
          type="number"
          min={1}
          step={1}
          value={value.timerMinutes}
          disabled={disabled}
          onChange={(e) => update({ timerMinutes: e.target.value })}
          className="w-full max-w-xs rounded-lg border px-3 py-2 disabled:bg-gray-100"
          placeholder="اتركه فارغاً بدون مؤقت"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">درجة النجاح %</label>
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={value.passDegree}
          disabled={disabled}
          onChange={(e) => update({ passDegree: e.target.value })}
          className="w-full max-w-xs rounded-lg border px-3 py-2 disabled:bg-gray-100"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={value.isRetake}
          disabled={disabled}
          onChange={(e) => update({ isRetake: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300"
        />
        السماح بإعادة الاختبار
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={value.isScoreDisplayed}
          disabled={disabled}
          onChange={(e) => update({ isScoreDisplayed: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300"
        />
        إظهار الدرجة للطالب
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={value.isAnswerDisplayed}
          disabled={disabled}
          onChange={(e) => update({ isAnswerDisplayed: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300"
        />
        إظهار الإجابات الصحيحة بعد التسليم
      </label>
    </div>
  );
};
